import { tv } from "tailwind-variants";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, InferType, number } from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { t } from "i18next";
import { Trans } from "react-i18next";

import {
  useResendVerifyExisting,
  useUser,
  useVerifyExisting,
  useVerifyExistingCode,
} from "@/requests/hooks/user";
import useIsMobile from "@/utils/useIsMobile";
import GradientWaitingRoom from "@/components/GradientWaitingRoom";
import { Input } from "@/components/Input";
import { Button } from "@/components/Button";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import formatError from "@/utils/formatError.ts";
import { getOnboardingUrl } from "@/utils/onboardingManager.ts";
import { COMPLY_HOME_URL } from "@/utils/const.ts";

const styles = tv({
  slots: {
    base: "mt-50 flex w-full flex-grow flex-col items-center justify-center",
    inputSection:
      "flex w-full flex-col gap-20 text-center md:flex-row md:items-start",
    input: "min-w-full md:min-w-[360px]",
    form: "w-full",
    buttonSection: "mt-5 w-full",
    resendSection: "mt-40 flex flex-row items-center justify-center gap-10",
    bold: "font-bold",
  },
});

const EmailVerifyExisting = () => {
  const {
    base,
    inputSection,
    buttonSection,
    resendSection,
    bold,
    input,
    form,
  } = styles();

  const [allowResendEmail, setAllowResendEmail] = useState<boolean>(false);
  const [secondsRemaining, setSecondsRemaining] = useState<number>(60);
  const [isWaiting, setIsWaiting] = useState(false);

  const { data: userData, refetch: refetchUser } = useUser({
    enabled: false,
  });

  const navigate = useNavigate();

  const {
    mutate: verifyExisting,
    data: verifyExistingData,
    isPending: isVerifyExistingPending,
    isSuccess: isVerifyExistingSuccess,
  } = useVerifyExisting();

  const {
    mutate: resendVerifyExistingEmail,
    isPending: isResendVerifyExistingEmailPending,
    isSuccess: isResendVerifyExistingEmailSuccess,
  } = useResendVerifyExisting();

  const {
    mutate: verifyExistingCode,
    error: verifyExistingCodeError,
    isPending: isVerifyExistingCodePending,
    isSuccess: isVerifyExistingCodeSuccess,
  } = useVerifyExistingCode();

  const emailFormSchema = object({
    email: string()
      .email()
      .required(t("settings.widgetSettings.privacyDetails.email.error"))
      .nullable(),
  });
  const codeFormSchema = object({
    code: number()
      .required(t("auth.verifyEmail.errors.empty"))
      .min(6, t("auth.verifyEmail.errors.tooShort")),
  });

  type IEmailVerifyExistingForm = InferType<typeof emailFormSchema>;
  type ICodeVerifyExistingForm = InferType<typeof codeFormSchema>;

  const { state } = useLocation();
  const isMobile = useIsMobile();

  const {
    register: registerVerifyEmail,
    handleSubmit: handleVerifyEmailSubmit,
  } = useForm<IEmailVerifyExistingForm>({
    resolver: yupResolver(emailFormSchema),
  });

  const {
    register: registerVerifyCode,
    handleSubmit: handleVerifyCodeSubmit,
    formState: { errors },
  } = useForm<ICodeVerifyExistingForm>({
    resolver: yupResolver(codeFormSchema),
  });

  const onEmailSubmit = ({ email }: IEmailVerifyExistingForm) => {
    if (null !== email) {
      verifyExisting({
        email,
      });
    }
  };

  const onClickResend = () => {
    resendVerifyExistingEmail({
      verificationToken: verifyExistingData.verificationToken,
    });
  };

  const onCodeSubmit = ({ code }: ICodeVerifyExistingForm) => {
    if (null !== code) {
      verifyExistingCode({
        code: code,
        verificationToken: verifyExistingData.verificationToken,
      });
    }
  };

  useEffect(() => {
    if (!userData) return;

    if (userData?.activeOnboardingFlow === "MARKETING") {
      navigate(getOnboardingUrl("PROFILE"));
    } else {
      navigate(COMPLY_HOME_URL);
    }
  }, [userData]);

  useEffect(() => {
    if (isVerifyExistingCodeSuccess) {
      refetchUser();
    }
  }, [isVerifyExistingCodeSuccess]);

  useEffect(() => {
    setIsWaiting(
      isResendVerifyExistingEmailPending ||
        isVerifyExistingPending ||
        isVerifyExistingPending,
    );
  }, [
    isResendVerifyExistingEmailPending,
    isVerifyExistingPending,
    isVerifyExistingPending,
  ]);

  useEffect(() => {
    if (isVerifyExistingSuccess) {
      toast.success(t("auth.verifyEmailExisting.sendSuccess"));

      // If the response was a success, start a timer that'll allow them to resend in 1 min.
      const countdownInterval = setInterval(() => {
        if (secondsRemaining > 0) {
          setSecondsRemaining(secondsRemaining => secondsRemaining - 1);
        }
      }, 1000);

      setTimeout(() => {
        setAllowResendEmail(true);
      }, 60000);

      return () => {
        clearInterval(countdownInterval);
      };
    }
  }, [isVerifyExistingSuccess]);

  useEffect(() => {
    if (isResendVerifyExistingEmailSuccess) {
      toast.success(t("auth.verifyEmailExisting.resendSuccess"));
    }
  }, [isResendVerifyExistingEmailSuccess]);

  const renderDescription = () => (
    <Trans
      i18nKey="auth.verifyEmailExisting.heading.description"
      values={{ email: state?.email }}>
      zero
      <span className={bold()}>{state?.email}</span>
      two
    </Trans>
  );

  const renderResendSection = () => (
    <div className={resendSection()}>
      {t("auth.verifyEmailExisting.resendLabel")}
      <Button
        variant="link"
        width="unset"
        loading={isResendVerifyExistingEmailPending}
        onClick={onClickResend}
        testId="resend-verify-cta"
        disabled={!allowResendEmail || isResendVerifyExistingEmailPending}>
        {!allowResendEmail
          ? t("auth.verifyEmailExisting.countdown", { secondsRemaining })
          : t("auth.verifyEmailExisting.resendCta")}
        <FontAwesomeIcon icon={faCaretRight} />
      </Button>
    </div>
  );

  const renderEmailForm = () => (
    <div className={base()}>
      <div className={inputSection()}>
        <form
          onSubmit={handleVerifyEmailSubmit(onEmailSubmit)}
          id="email-verify-existing-form"
          data-testid="email-verify-existing-form"
          className={form()}>
          <div className={input()}>
            <Input
              {...registerVerifyEmail("email")}
              type="text"
              label={t("auth.verifyEmailExisting.inputLabel")}
              testId="email-verify-input"
              helperTextTestId="verify-email-existing-helper-text"
            />
          </div>
        </form>
        <div className={buttonSection()}>
          <Button
            variant="primary"
            type="submit"
            loading={isVerifyExistingPending}
            disabled={isVerifyExistingPending}
            width={isMobile ? "full" : "lg"}
            data-testid="submit-verify-cta"
            form="email-verify-existing-form">
            {t("auth.verifyEmailExisting.submitCta")}
          </Button>
        </div>
      </div>
    </div>
  );

  const getHelperText = () => {
    if (!!errors?.code?.message) return errors.code.message;

    if (!verifyExistingCodeError) return;

    const { codes } = formatError(verifyExistingCodeError);

    if (codes?.includes("EXPIRED_VERIFICATION_CODE")) {
      return t("auth.verifyEmail.errors.expired");
    } else if (codes?.includes("UNABLE_TO_VERIFY_CODE")) {
      return t("auth.verifyEmail.errors.unableToVerify");
    } else if (codes?.includes("INVALID_VERIFICATION_CODE")) {
      return t("auth.verifyEmail.errors.invalid");
    }
  };

  const renderSubmitCode = () => (
    <div className={base()}>
      <div className={inputSection()}>
        <form
          onSubmit={handleVerifyCodeSubmit(onCodeSubmit)}
          id="email-existing-code-verify-form"
          data-testid="email-existing-code-verify-form"
          className={form()}>
          <div className={input()}>
            <Input
              {...registerVerifyCode("code")}
              type="text"
              label={t("auth.verifyEmail.inputLabel")}
              helperText={getHelperText()}
              variant={!!getHelperText() ? "error" : undefined}
              testId="email-existing-code-verify-input"
              aria-invalid={!!errors?.code}
              maxLength={6}
              helperTextTestId="verify-email-existing-helper-text"
            />
          </div>
        </form>
        <div className={buttonSection()}>
          <Button
            variant="primary"
            type="submit"
            width={isMobile ? "full" : "lg"}
            data-testid="submit-verify-cta"
            form="email-existing-code-verify-form"
            loading={isVerifyExistingCodePending}
            disabled={isVerifyExistingCodePending}>
            {t("auth.verifyEmail.submitCta")}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <GradientWaitingRoom
      isWaiting={isWaiting}
      headingText={t("auth.verifyEmailExisting.heading.header")}
      descriptionText={renderDescription()}
      whiteBackground={true}>
      {isVerifyExistingSuccess ? (
        <>
          {renderSubmitCode()}
          {renderResendSection()}
        </>
      ) : (
        renderEmailForm()
      )}
    </GradientWaitingRoom>
  );
};

export default EmailVerifyExisting;
